import { ApiError } from '@/shared/types/api.types';
import { abortReason } from './multipartUpload';
import type { PartTransport } from './multipartUpload';

/** Long enough for a 16 MiB part at about 1 Mbit/s. */
const PART_TIMEOUT_MS = 150_000;

function storageError(status: number): ApiError {
  const message = `Storage refused the part (HTTP ${status}).`;
  if (status === 403) return new ApiError('forbidden', message, { status });
  if (status === 404) return new ApiError('notFound', message, { status });
  if (status === 408) return new ApiError('timeout', message, { status });
  // S3 throttles with 503 SlowDown; other providers use 429. Both pass after a pause.
  if (status === 429 || status >= 500) return new ApiError('server', message, { status });
  return new ApiError('unknown', message, { status });
}

/**
 * PUTs parts with `XMLHttpRequest`, the only browser API that reports upload progress.
 *
 * It sets no headers at all. A bearer token would leak the user's session to the storage
 * provider, and S3 refuses a request that carries both a presigned query and an
 * `Authorization` header. That is also why parts bypass `apiClient`, its interceptors and
 * its 30 s timeout.
 */
export function createXhrPartTransport(
  options: { readonly timeoutMs?: number; readonly createRequest?: () => XMLHttpRequest } = {}
): PartTransport {
  const timeoutMs = options.timeoutMs ?? PART_TIMEOUT_MS;
  const createRequest = options.createRequest ?? ((): XMLHttpRequest => new XMLHttpRequest());

  return {
    put(url, body, { signal, onProgress }) {
      return new Promise<string>((resolve, reject) => {
        if (signal.aborted) {
          reject(abortReason(signal));
          return;
        }

        const xhr = createRequest();
        const onAbort = (): void => xhr.abort();
        const settle = (outcome: () => void): void => {
          signal.removeEventListener('abort', onAbort);
          outcome();
        };

        xhr.open('PUT', url);
        xhr.timeout = timeoutMs;
        xhr.upload.onprogress = (event): void => onProgress(event.loaded);
        xhr.onload = (): void =>
          settle(() => {
            if (xhr.status < 200 || xhr.status >= 300) {
              reject(storageError(xhr.status));
              return;
            }
            // Readable cross-origin only when the bucket's CORS rules expose it.
            const etag = xhr.getResponseHeader('ETag');
            if (etag === null || etag.length === 0) {
              reject(
                new ApiError(
                  'unknown',
                  'Storage did not expose the part ETag; its CORS rules must expose ETag.',
                  {
                    status: xhr.status,
                    code: 'storage_cors_misconfigured',
                  }
                )
              );
              return;
            }
            resolve(etag);
          });
        xhr.onerror = (): void =>
          settle(() => reject(new ApiError('network', 'The connection to storage was lost.')));
        xhr.ontimeout = (): void =>
          settle(() => reject(new ApiError('timeout', 'Uploading a part timed out.')));
        xhr.onabort = (): void => settle(() => reject(abortReason(signal)));

        signal.addEventListener('abort', onAbort, { once: true });
        xhr.send(body);
      });
    },
  };
}
